import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';

/**
 * VerifyEmailPage Component
 *
 * Handles email verification link clicks from verification emails.
 * Redirects to the backend endpoint which handles verification and redirects to login.
 *
 * URL: /verify-email?token=xxx
 *
 * @validates Requirements 2.1, 2.3, 2.4, 2.5, 2.6, 5.12, 7.5, 7.6
 */
export function VerifyEmailPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [errorType, setErrorType] = useState<'invalid' | 'expired' | null>(null);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorType('invalid');
      return;
    }

    // Redirect to backend endpoint which handles verification and redirects to login
    window.location.href = `/api/auth/verify-email?token=${encodeURIComponent(token)}`;
  }, [token]);

  // This page mainly shows a loading state before redirecting
  return (
    <div className="max-w-md mx-auto py-16 text-center">
      <div className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-primary-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              {t('verifyEmail.loading.title')}
            </h1>
            <p className="text-slate-600">
              {t('verifyEmail.loading.message')}
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">
              {errorType === 'expired' ? t('verifyEmail.error.expired.title') : t('verifyEmail.error.invalid.title')}
            </h1>
            <p className="text-slate-600 mb-6">
              {errorType === 'expired'
                ? t('verifyEmail.error.expired.message')
                : t('verifyEmail.error.invalid.message')}
            </p>
            <Link to="/login" className="btn-primary inline-block">
              {t('verifyEmail.error.backToLogin')}
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
